import React from 'react';

export function createKeyboardHandler(
  keyActions: Record<string, (e: React.KeyboardEvent) => void>,
) {
  return (e: React.KeyboardEvent) => {
    const action = keyActions[e.key];
    if (action) {
      action(e);
    }
  };
}

export function createFocusHandler() {
  return (e: React.FocusEvent) => e.stopPropagation();
}

export function createMouseOverHandler<TData = any>(
  callback: (data: TData, event: React.MouseEvent<HTMLElement>) => void,
) {
  return (data: TData) => (event: React.MouseEvent<HTMLElement>) => {
    callback(data, event);
  };
}

export function createMouseLeaveHandler(callback: () => void) {
  return callback;
}

export function createFieldHandler<T>(shouldHandle: (field: string) => boolean, handler: T) {
  return (field: string): T | undefined => {
    return shouldHandle(field) ? handler : undefined;
  };
}

export const keyboardActions = {
  stopEscapePropagation: (e: React.KeyboardEvent) => {
    e.stopPropagation();
  },

  preventDefault: (e: React.KeyboardEvent) => {
    e.preventDefault();
  },

  stopPropagation: (e: React.KeyboardEvent) => {
    e.stopPropagation();
  },
};

export const eventUtils = {
  escapeKeyHandler: createKeyboardHandler({
    Escape: keyboardActions.stopEscapePropagation,
  }),
  focusStopPropagation: createFocusHandler(),
  createKeyboardHandler,
  createFieldHandler,
  createMouseOverHandler,
  createMouseLeaveHandler,
};

export const useScrollableDetection = <TItem>(items: TItem[], threshold = 3) => {
  return items.length > threshold;
};

export const useCellEditability = <TRow>(
  checkPermissions: (row: TRow, field: string) => boolean,
) => {
  return { isCellEditable: checkPermissions };
};

export interface RowTransformConfig<TSource, TRow> {
  getId: (item: TSource) => string;
  transform: (item: TSource) => TRow;
  filter?: (item: TSource) => boolean;
}

export const transformToRows = <TSource, TRow>(
  items: TSource[],
  config: RowTransformConfig<TSource, TRow>,
): TRow[] => {
  if (!items.length) return [];

  const filteredItems = config.filter ? items.filter(config.filter) : items;

  return filteredItems.map((item) => ({
    id: config.getId(item),
    ...config.transform(item),
  }));
};
export interface CellPropsConfig {
  view?: Record<string, any>;
  edit?: Record<string, any>;
  common?: Record<string, any>;
}

export const createCellPropsFactory = (configs: Record<string, CellPropsConfig>) => {
  return {
    getViewProps: (type: string) => ({
      ...configs[type]?.common,
      ...configs[type]?.view,
    }),
    getEditProps: (type: string) => ({
      ...configs[type]?.common,
      ...configs[type]?.edit,
    }),
  };
};

export const commonCellProps = {
  focusStop: {
    onFocus: createFocusHandler(),
  },
  editMode: {
    disableEscapeKeyDown: true,
  },
  viewMode: {
    focused: false,
  },
  tableView: {
    tableView: true,
  },
};
